'use client'

import {
  AUTH_COOKIE_AUTHENTICATION_MODE_KEY,
  AUTH_COOKIE_PUNCHOUT_TOKEN_KEY,
  AUTH_COOKIE_SESSION_EXPIRED_KEY,
} from '@/feature/auth/services/auth.constant'
import { ROUTE_AUTH_TOKEN_REFRESH } from '@/lib/next/authentication/authentication.constants'
import { setContext } from '@apollo/client/link/context'
import { getCookies } from 'cookies-next/client'
import { extractHeadersFromCookies } from '../utils'

/**
 * When multiple requests are made while the access token is expired, we want to avoid multiple refreshes.
 * This variable holds a promise for the refresh operation, so that subsequent requests can await it instead
 * of triggering their own refresh.
 */
let refreshAuthTokenPromise: Promise<'ok' | 'failed'> | null = null

/**
 * Utility function to request a refresh of the authentication token.
 * The call will attach freshly generated authentication headers to the request (httpOnly auth-token).
 */
function requestRefreshAuthToken(): Promise<'ok' | 'failed'> {
  if (!refreshAuthTokenPromise) {
    refreshAuthTokenPromise = fetch(ROUTE_AUTH_TOKEN_REFRESH, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`Refresh failed: ${res.status}`)

        return 'ok' as const
      })
      .finally(() => {
        refreshAuthTokenPromise = null
      })
  }

  return refreshAuthTokenPromise
}

/**
 * Creates a client Apollo Link that adds authentication headers to GraphQL requests based on cookies.

 *
 * @returns An Apollo Link that adds available authentication headers to each request.
 *          If no cookies are provided, returns a link with empty context.
 */
export const makeBrowserAuthLink = () =>
  setContext(async (_, context) => {
    const cookies = getCookies() as Record<string, string | undefined> | undefined

    if (!cookies) {
      console.warn('No cookies found. Returning context without auth headers.')

      return context
    }

    context.headers = context.headers || {}

    const correlationId = cookies['x-correlation-id']

    if (correlationId) {
      context.headers['x-correlation-id'] = correlationId
    }

    const authMode = cookies[AUTH_COOKIE_AUTHENTICATION_MODE_KEY]
    const isImpersonating = authMode === 'impersonate'
    const isInternal = authMode === 'internal'
    const isPunchout = authMode === 'punchout'
    const wasAuth = cookies[AUTH_COOKIE_SESSION_EXPIRED_KEY] === 'true'
    const hasValidAuthToken = !!cookies['auth-token'] || !!cookies[AUTH_COOKIE_PUNCHOUT_TOKEN_KEY]

    if (wasAuth && !hasValidAuthToken) {
      if (!isImpersonating && !isInternal && !isPunchout) {
        // in impersonation, internal or punchout mode, we can't refresh the standard customer token
        await requestRefreshAuthToken()
      }
    }

    for (const [cookieName, cookieValue] of Object.entries(extractHeadersFromCookies(cookies))) {
      context.headers[cookieName] = cookieValue
    }

    return context
  })
